import { followTeamAddress, isEventOf, keys, movedRoute, rememberTeamAddresses, workspacePrefix, type Runtime } from "@baton/core";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { listenScreenEvents } from "../realtime/screenEvents.ts";

/**
 * A Team whose address changed (001 FR-157, FR-158, 2026-09-29), or switched back to the one it
 * held. The old address redirects nowhere and another Team may take it once the Team no longer
 * holds it, so this device follows the Team itself: what it keeps under the old address moves to
 * the new one, the cache read under the old address is dropped rather than shown under a Team that
 * may take the address later, and a person inside the Team is taken to the same screen at
 * the new address. The owner who changes it is followed by the change's own answer, every other
 * signed-in member by the `workspace.updated` event on the Team's stream, and a device that saw
 * neither by its next read of `GET /me`.
 */
export function followTeamMove(runtime: Runtime, workspaceId: string, from: string, to: string): void {
  followTeamAddress(runtime.storage, workspaceId, from, to);
  runtime.queryClient.removeQueries({ queryKey: workspacePrefix(from) });
}

/** Follow every Team the `/me` read shows at an address this device did not last see it at. */
export function followMovedTeams(runtime: Runtime, memberships: readonly { readonly workspace_id: string; readonly slug: string }[]): void {
  for (const move of rememberTeamAddresses(runtime.storage, memberships)) runtime.queryClient.removeQueries({ queryKey: workspacePrefix(move.from) });
}

/**
 * While a screen of the Team at `ws` is open, follow a change of its address that arrives on the
 * stream: move what is kept, re-read who the person is in the Team, and replace the route with the
 * same screen at the new address, telling the person why the address bar changed through `onMoved`.
 */
export function useFollowTeamAddress(runtime: Runtime, ws: string, workspaceId: string | null, onMoved: (to: string) => void): void {
  const navigate = useNavigate();
  const location = useLocation();
  const here = `${location.pathname}${location.search}${location.hash}`;
  useEffect(() => {
    if (workspaceId === null) return;
    return listenScreenEvents(runtime, (envelope) => {
      if (!isEventOf(envelope, "workspace.updated")) return;
      const { id, slug } = envelope.data.workspace;
      if (id !== workspaceId || typeof slug !== "string" || slug === ws) return;
      followTeamMove(runtime, workspaceId, ws, slug);
      void runtime.queryClient.invalidateQueries({ queryKey: keys.global.me() });
      void navigate(movedRoute(here, ws, slug), { replace: true });
      onMoved(slug);
    });
  }, [runtime, ws, workspaceId, navigate, here, onMoved]);
}
